import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { getUserData } from '../utils';
import type { QuoteTemplate } from '../types';
import {
  dealLinesFromRows,
  pickPreviewTemplate,
  splitBySuitability,
  type DealLines,
} from '../utils/quoteTemplateScope';

/**
 * I template preventivo utilizzabili per una specifica offerta.
 *
 * Prima ognuno dei punti che sceglie un template si arrangiava: due leggevano
 * lo store, due facevano una query propria, due sceglievano da soli "il piu'
 * recente" senza chiedere niente. Qui la logica sta in un posto e le righe
 * dell'offerta si caricano una volta sola — serve, perche' tre di quei punti
 * (allegato email, firma, anteprima impostazioni) non hanno i servizi in mano.
 *
 * Spec: MD/features/2026-08-12-template-preventivo-per-servizio.md
 */

interface OrgTemplateData {
  templates: QuoteTemplate[];
  defaultTemplateId: string | null;
  allowUnsuitable: boolean;
}

// Cache per organizzazione: la lista offerte monta una card per riga e senza
// cache ogni miniatura rifarebbe le stesse due query.
let _orgCache: Promise<OrgTemplateData> | null = null;
const _linesCache = new Map<string, DealLines>();

export function invalidateTemplatesForDealCache(dealId?: string): void {
  if (dealId) _linesCache.delete(dealId);
  else {
    _linesCache.clear();
    _orgCache = null;
  }
}

async function loadOrgTemplates(): Promise<OrgTemplateData> {
  if (_orgCache) return _orgCache;
  _orgCache = (async () => {
    const userData = await getUserData();
    const orgId = userData?.organizationId;
    if (!orgId) return { templates: [], defaultTemplateId: null, allowUnsuitable: true };

    const [tplRes, scopeRes, setRes] = await Promise.all([
      supabase
        .from('quote_templates')
        .select('*')
        .eq('document_kind', 'quote')
        .eq('organization_id', orgId)
        .is('archived_at', null)
        .order('created_at', { ascending: false }),
      supabase
        .from('quote_template_services')
        .select('template_id, service_id')
        .eq('organization_id', orgId),
      supabase
        .from('settings')
        .select('default_quote_template_id, allow_unsuitable_quote_templates')
        .eq('organization_id', orgId)
        .maybeSingle(),
    ]);

    const byTemplate = new Map<string, string[]>();
    for (const row of scopeRes.data || []) {
      const list = byTemplate.get(row.template_id) || [];
      list.push(row.service_id);
      byTemplate.set(row.template_id, list);
    }

    return {
      templates: (tplRes.data || []).map((t: QuoteTemplate) => ({
        ...t,
        scopedServiceIds: byTemplate.get(t.id) || [],
      })),
      defaultTemplateId: setRes.data?.default_quote_template_id ?? null,
      // Se l'impostazione manca (org senza riga settings) si resta permissivi:
      // 95 org su 106 hanno un solo template e bloccarle sarebbe un vicolo cieco.
      allowUnsuitable: setRes.data?.allow_unsuitable_quote_templates ?? true,
    };
  })();
  return _orgCache;
}

async function loadDealLines(dealId: string): Promise<DealLines> {
  const cached = _linesCache.get(dealId);
  if (cached) return cached;

  // L'isolamento passa da `deals`: su deal_services la colonna organization_id
  // e' stata riempita solo il 12/08/2026 e la RLS copre comunque entrambe le
  // strade. Qui basta il filtro per offerta.
  const { data } = await supabase
    .from('deal_services')
    .select('line_type, service_id')
    .eq('deal_id', dealId);

  const lines = dealLinesFromRows(data);
  _linesCache.set(dealId, lines);
  return lines;
}

export interface TemplatesForDeal {
  /** Template adatti all'offerta, dal piu' recente. */
  suitable: QuoteTemplate[];
  /** Non adatti: da mostrare solo dietro "mostra tutti". */
  unsuitable: QuoteTemplate[];
  /** false = l'organizzazione vieta di usare template non adatti. */
  allowUnsuitable: boolean;
  /** Il template da preselezionare / usare per l'anteprima. null = segnaposto. */
  preferred: QuoteTemplate | null;
  defaultTemplateId: string | null;
  loading: boolean;
  error: string | null;
}

const VUOTO: DealLines = { serviceIds: [], hasCustomLines: false };

/**
 * @param dealId  offerta di riferimento; null = nessun filtro (tutti adatti)
 * @param rows    righe gia' in mano al chiamante (DealDetailsPage le ha):
 *                evita la query
 */
export function useTemplatesForDeal(
  dealId: string | null | undefined,
  rows?: Array<{ line_type?: string | null; service_id?: string | null }> | null,
): TemplatesForDeal {
  const [org, setOrg] = useState<OrgTemplateData | null>(null);
  const [lines, setLines] = useState<DealLines | null>(rows ? dealLinesFromRows(rows) : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Le righe passate dal chiamante hanno la precedenza e non costano query.
  const rowsKey = rows ? JSON.stringify(rows.map((r) => [r.line_type, r.service_id])) : null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const orgData = await loadOrgTemplates();
        if (cancelled) return;
        setOrg(orgData);

        if (rowsKey !== null) {
          setLines(dealLinesFromRows(rows));
        } else if (dealId) {
          const l = await loadDealLines(dealId);
          if (!cancelled) setLines(l);
        } else {
          setLines(VUOTO);
        }
        if (!cancelled) setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, rowsKey]);

  return useMemo(() => {
    const templates = org?.templates ?? [];
    const l = lines ?? VUOTO;
    const { suitable, unsuitable } = splitBySuitability(templates, l);
    return {
      suitable,
      unsuitable,
      allowUnsuitable: org?.allowUnsuitable ?? true,
      preferred: pickPreviewTemplate(templates, l, org?.defaultTemplateId),
      defaultTemplateId: org?.defaultTemplateId ?? null,
      loading,
      error,
    };
  }, [org, lines, loading, error]);
}
